import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * Public API documentation (section 13.7).
 *
 * The paths and the status codes are the machine-readable part and are written
 * once here; every *description* is looked up in the translation catalogue, so a
 * developer reading the page in Portuguese is not handed English prose in the
 * middle of a Portuguese document (section 10.2).
 */

/** The path is not language-dependent; the note is a `docs.endpoints.<key>`. */
const ENDPOINTS = [
  { key: 'lookup', method: 'GET', path: '/v1/lookup/{country}/{identifier}' },
  { key: 'entity', method: 'GET', path: '/v1/entity/{country}/{identifier}' },
  { key: 'relations', method: 'GET', path: '/v1/entity/{country}/{identifier}/relations' },
  { key: 'risk', method: 'GET', path: '/v1/risk/{country}/{identifier}' },
  { key: 'usage', method: 'GET', path: '/v1/usage' },
  { key: 'sources', method: 'GET', path: '/v1/sources' },
] as const;

/** Same for the error table: the code is data, the meaning is a `docs.errors.<code>`. */
const ERRORS = [401, 403, 404, 402, 429] as const;

export function DocsPage(): React.ReactNode {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-3xl font-bold tracking-tight">{t('docs.title')}</h1>
      <p className="mt-2 text-[var(--color-muted-fg)]">{t('docs.subtitle')}</p>

      <section className="mt-8">
        <h2 className="text-xl font-bold">{t('docs.authTitle')}</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('docs.authBody')}</p>
        <pre className="mt-3 overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-4 text-xs">
          <code>{`Authorization: Bearer foco_live_...`}</code>
        </pre>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">{t('docs.endpointsTitle')}</h2>
        <ul className="mt-4 space-y-3">
          {ENDPOINTS.map((endpoint) => (
            <li key={endpoint.path} className="rounded-[var(--radius-card)] border border-[var(--color-border)] p-3">
              <p className="font-mono text-xs">
                <span className="mr-2 rounded bg-brand-700/10 px-1.5 py-0.5 font-semibold text-brand-700">
                  {endpoint.method}
                </span>
                {endpoint.path}
              </p>
              <p className="mt-1 text-sm text-[var(--color-muted-fg)]">
                {t(`docs.endpoints.${endpoint.key}`)}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">{t('docs.exampleTitle')}</h2>
        <pre className="mt-3 overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-4 text-xs">
          <code>{`curl -s -H "Authorization: Bearer foco_live_..." \\
  https://foco.arriba.digital/v1/entity/br/19131243000197

{
  "countryCode": "BR",
  "identifier": "19131243000197",
  "legalName": "OPEN KNOWLEDGE BRASIL",
  "status": "active",
  "risk": { "level": "medium", "score": 25, "factors": [ ... ] },
  "sources": [ { "code": "br.brasilapi.cnpj", "fetchedAt": "2026-10-04T..." } ]
}`}</code>
        </pre>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">{t('docs.errorsTitle')}</h2>
        <table className="mt-4 w-full border-collapse text-sm">
          <tbody>
            {ERRORS.map((code) => (
              <tr key={code} className="border-b border-[var(--color-border)]">
                <th scope="row" className="w-16 py-2 pr-4 text-left font-mono font-semibold">
                  {code}
                </th>
                <td className="py-2 text-[var(--color-muted-fg)]">{t(`docs.errors.${code}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">{t('docs.limitsTitle')}</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('docs.limitsBody')}</p>
        <pre className="mt-3 overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-4 text-xs">
          <code>{`X-RateLimit-Limit: 60
X-RateLimit-Remaining: 57
X-RateLimit-Reset: 1728038400`}</code>
        </pre>
      </section>

      <p className="mt-8 text-sm text-[var(--color-muted-fg)]">
        <Link to="/pricing" className="text-brand-600 hover:underline">
          {t('docs.plansLink')}
        </Link>
      </p>
    </div>
  );
}
